import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useConsentStore } from '@/stores/consentStore';
import api from '@/services/api';
import { DEV_MODE } from '@/lib/devMode';
import { AIDisclosureModal } from '@/components/AIDisclosureModal';

interface ConsentGateProps {
  children: React.ReactNode;
}

export default function ConsentGate({ children }: ConsentGateProps) {
  const { isAuthenticated } = useAuthStore();
  const [tosAccepted, setTosAccepted] = useState(true); // optimistic default
  const [aiDisclosureAccepted, setAiDisclosureAccepted] = useState(true); // optimistic default
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);
  const setConsent = useConsentStore((s) => s.setConsent);

  useEffect(() => {
    if (DEV_MODE || !isAuthenticated) {
      setChecked(true);
      setConsent({ tosAccepted: true, aiDisclosureAccepted: true, resolved: true });
      return;
    }

    api.consentStatus()
      .then(({ tos_accepted, ai_disclosure_accepted }) => {
        setTosAccepted(tos_accepted);
        setAiDisclosureAccepted(ai_disclosure_accepted);
        setConsent({ tosAccepted: tos_accepted, aiDisclosureAccepted: ai_disclosure_accepted, resolved: true });
      })
      .catch(() => {
        // If the check fails, don't block the user
        setTosAccepted(true);
        setAiDisclosureAccepted(true);
        setConsent({ tosAccepted: true, aiDisclosureAccepted: true, resolved: true });
      })
      .finally(() => {
        setChecked(true);
      });
  }, [isAuthenticated, setConsent]);

  const handleAccept = async () => {
    setIsAccepting(true);
    setError(null);
    try {
      await api.acceptTerms();
      setTosAccepted(true);
      setConsent({ tosAccepted: true });
    } catch {
      setError('Failed to record your acceptance. Please try again.');
    } finally {
      setIsAccepting(false);
    }
  };

  // Don't render anything until we've checked (avoid flash)
  if (!checked) return null;

  return (
    <>
      {children}

      {!tosAccepted && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-dark-surface rounded-lg border-2 border-hairline dark:border-dark-border p-8 shadow-xl w-full max-w-md">
            <h2 className="text-xl font-semibold text-ink dark:text-dark-text mb-3">
              Updated Terms of Service
            </h2>
            <p className="text-steel-dark dark:text-dark-muted text-sm mb-5">
              We've updated our{' '}
              <a href="/terms" target="_blank" className="text-ink hover:underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" target="_blank" className="text-ink hover:underline">
                Privacy Policy
              </a>
              . Please review and accept them to continue using Zupa.
            </p>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-error-bg dark:bg-red-900/20 border border-error-border dark:border-red-800 text-error-text dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <button
              onClick={handleAccept}
              disabled={isAccepting}
              className="w-full py-3 rounded-md bg-cargo text-cargo-ink font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {isAccepting ? 'Saving...' : 'I Accept'}
            </button>
          </div>
        </div>
      )}

      {tosAccepted && !aiDisclosureAccepted && (
        <AIDisclosureModal onAccepted={() => {
          setAiDisclosureAccepted(true);
          setConsent({ aiDisclosureAccepted: true });
        }} />
      )}
    </>
  );
}
